import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { BOOKING_TYPES } from "@/data/content";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "hochzeit", date: "", budget: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Vielen Dank! Ihre Nachricht ist angekommen.", {
        description: "Ich antworte persönlich — meist noch am selben Tag.",
      });
      setForm({ name: "", email: "", phone: "", category: "hochzeit", date: "", budget: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <section id="kontakt" data-testid="contact-section" className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-36">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5"
        >
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.35em] text-gold">Kontakt — Unverbindlich anfragen</p>
          <h2 className="font-serif text-4xl font-light leading-tight tracking-tight text-white sm:text-5xl">
            Erzählen Sie mir <span className="italic text-zinc-400">Ihre Geschichte.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400">
            Ob Hochzeit, Firmenevent, Business-Shooting oder euer Vierbeiner vor der Linse —
            jedes Projekt beginnt mit einem Gespräch.
          </p>

          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <MapPin size={16} className="mt-1 text-gold" />
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Einsatzgebiet</p>
                <p className="mt-1 text-sm text-zinc-300">
                  Ulm · Neu-Ulm · Vöhringen
                  <br />
                  Günzburg · Bopfingen & Umgebung
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail size={16} className="mt-1 text-gold" />
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">E-Mail</p>
                <a href="mailto:eric779fotos@gmail.com" data-testid="contact-email-link" className="mt-1 block text-sm text-zinc-300 transition-colors hover:text-gold">
                  eric779fotos@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MessageCircle size={16} className="mt-1 text-gold" />
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">WhatsApp</p>
                <a href="https://wa.me/4917655085770" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="mt-1 block text-sm text-zinc-300 transition-colors hover:text-gold">
                  +49 176 55085770
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          onSubmit={submit}
          className="lg:col-span-7"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
            <div>
              <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Name *</label>
              <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Ihr vollständiger Name" className="input-line" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">E-Mail *</label>
              <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="hallo@beispiel.de" className="input-line" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Telefon</label>
              <input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="+49 …" className="input-line" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Kategorie</label>
              <select data-testid="contact-category-select" value={form.category} onChange={set("category")} className="input-line cursor-pointer">
                {BOOKING_TYPES.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Wunschdatum</label>
              <input data-testid="contact-date-input" type="date" value={form.date} onChange={set("date")} className="input-line [color-scheme:dark]" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Geschätztes Budget</label>
              <select data-testid="contact-budget-select" value={form.budget} onChange={set("budget")} className="input-line cursor-pointer">
                <option value="">Bitte wählen</option>
                <option value="< 1.000 €">unter 1.000 €</option>
                <option value="1.000 – 2.500 €">1.000 – 2.500 €</option>
                <option value="2.500 – 5.000 €">2.500 – 5.000 €</option>
                <option value="> 5.000 €">über 5.000 €</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Ihre Vision *</label>
              <textarea
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="Worum geht es? Was soll man spüren, wenn man die Bilder in zwanzig Jahren wieder ansieht?"
                className="input-line resize-none"
              />
            </div>
          </div>
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="mt-10 w-full rounded-full border border-gold/60 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink disabled:opacity-50 sm:w-auto sm:px-14"
          >
            {sending ? "Wird gesendet …" : "Nachricht senden"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
